import { ScreenHelp } from "@/components/Help";
import { ErrorState, LoadingState } from "@/components/States";
import { Card, Icon, Pill, SectionTitle } from "@/components/ui";
import type { CostMaster as CostMasterData } from "@/lib/api";
import { DEFAULT_SCRAP_RECOVERY_RATE } from "@/lib/cost/cncQuote";
import { useCostMaster } from "@/lib/hooks";
import { useIsMobile } from "@/lib/useViewport";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";

// 원가 마스터 (편집 가능) — 공정·설비(임율 포함)/원재료/표면처리/상수/견적설정 테이블을 렌더.
// 데이터는 백엔드 `/api/cost/master` (= `@/lib/cost/master` 미러)에서 온다.

const COST_MASTER_HELP = [
  "모든 마스터(공정·설비 / 원재료 / 표면처리 / 원가 상수 / 견적 설정)를 한 화면에서 관리합니다 — 구 CNC 마스터는 이 화면으로 통합되었습니다.",
  "공정 · 설비 마스터: 공정에 배정된 장비의 가액 · 전력용량 · 설치면적 · 인원 · 임율(₩/HR) — 견적 공정의 임율·기계경비가 여기서 파생됩니다.",
  "노출순서: 견적상세 분해표 · 엑셀의 공정 컬럼이 이 값의 오름차순으로 출력됩니다 — 작을수록 앞(예: 냉간단조(F) 10 · 냉간단조(PF-624) 15 · 냉간단조(H) 20). 저장하면 이 순서로 정렬됩니다.",
  "원재료: 견적 재질 선택지 — 단가(₩/g)와 비중(단중 = 비중/1000 자동). 표면처리: 견적 표면처리 단가(₩/g).",
  "원가 상수: 전력단가 · 부대설비비율 · 건축비용 · 상각년수 · 작업시간 · 간접경비율 등 전역 1행 값.",
  "견적 설정: 여유율 · 일반관리비율 · 재료관리비율 — 새 견적의 계산 기본값.",
  "저장 버튼으로 변경을 서버에 반영하고, 되돌리기로 마지막 저장값으로 복원합니다. 기존 견적은 작성 시점 값으로 고정되어 영향받지 않습니다.",
];

const thStyle: CSSProperties = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: "1px solid var(--bg-card)",
  whiteSpace: "nowrap",
};
const tdStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13.5,
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--bg-card-hover)",
  whiteSpace: "nowrap",
};
const tdNum: CSSProperties = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" };

const inputStyle: CSSProperties = {
  width: "100%",
  background: "var(--bg-input, var(--bg-card-hover))",
  border: "1px solid var(--border-subtle, var(--bg-card))",
  borderRadius: 4,
  color: "var(--text-strong)",
  fontVariantNumeric: "tabular-nums",
  fontSize: 13,
  padding: "3px 7px",
  textAlign: "right",
  boxSizing: "border-box",
  minWidth: 80,
};

const Table = ({ headers, children }: { headers: string[]; children: ReactNode }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={h} style={{ ...thStyle, textAlign: i === 0 ? "left" : "right" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const NumInput = ({
  value,
  onChange,
  disabled,
  style = inputStyle,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  disabled: boolean;
  style?: CSSProperties;
}) => (
  <input
    type="number"
    style={style}
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === "") {
        onChange(null);
      } else {
        const parsed = Number.parseFloat(raw);
        onChange(Number.isNaN(parsed) ? 0 : parsed);
      }
    }}
  />
);

const DelBtn = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title="행 삭제"
    style={{
      background: "transparent",
      border: 0,
      color: "var(--text-dim)",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.35 : 1,
      display: "inline-flex",
      padding: 4,
    }}
  >
    <Icon name="trash" size={15} />
  </button>
);

export const CostMaster = ({ onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  // 모바일에서 탭 영역 확보를 위해 입력칸 높이를 키운다(데스크톱은 그대로).
  const inp: CSSProperties = mobile ? { ...inputStyle, padding: "8px 7px" } : inputStyle;
  const { data, loading, error, reload, save } = useCostMaster();
  const [draft, setDraft] = useState<CostMasterData | null>(null);
  const [saving, setSaving] = useState(false);

  // Sync draft when server data arrives (first load + after save/reload)
  useEffect(() => {
    if (!loading && data) {
      setDraft(structuredClone(data));
    }
  }, [data, loading]);

  if (loading || draft === null) return <LoadingState label="원가 마스터 불러오는 중…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const handleSave = async () => {
    setSaving(true);
    try {
      await save(draft);
      onToast("원가 마스터가 저장되었습니다");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setDraft(structuredClone(data));
  };

  // Immutable updaters

  const setEquipment = (
    idx: number,
    patch: Partial<(typeof draft.processes)[number]["equipment"]>
  ) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const processes = prev.processes.map((p, i) =>
        i === idx ? { ...p, equipment: { ...p.equipment, ...patch } } : p
      );
      return { ...prev, processes };
    });
  };

  const setProcessDisplayOrder = (idx: number, displayOrder: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const processes = prev.processes.map((p, i) => (i === idx ? { ...p, displayOrder } : p));
      return { ...prev, processes };
    });
  };

  const setProcessLaborRate = (idx: number, laborRate: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const processes = prev.processes.map((p, i) => (i === idx ? { ...p, laborRate } : p));
      return { ...prev, processes };
    });
  };

  const setRawMaterial = (idx: number, patch: Partial<(typeof draft.rawMaterials)[number]>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const rawMaterials = prev.rawMaterials.map((r, i) => (i === idx ? { ...r, ...patch } : r));
      return { ...prev, rawMaterials };
    });
  };

  const setSurface = (idx: number, patch: Partial<(typeof draft.surfaceTreatments)[number]>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const surfaceTreatments = prev.surfaceTreatments.map((s, i) =>
        i === idx ? { ...s, ...patch } : s
      );
      return { ...prev, surfaceTreatments };
    });
  };

  const setQuoteSetting = (key: keyof typeof draft.quoteSettings, val: number) => {
    setDraft((prev) =>
      prev ? { ...prev, quoteSettings: { ...prev.quoteSettings, [key]: val } } : prev
    );
  };

  const setProcessKey = (idx: number, key: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const processes = prev.processes.map((p, i) => (i === idx ? { ...p, key } : p));
      return { ...prev, processes };
    });
  };

  const setEquipmentName = (idx: number, name: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const processes = prev.processes.map((p, i) =>
        i === idx ? { ...p, equipment: { ...p.equipment, name } } : p
      );
      return { ...prev, processes };
    });
  };

  const addProcess = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const newKey = `공정-${prev.processes.length + 1}`;
      const maxOrder = prev.processes.reduce((m, p) => Math.max(m, p.displayOrder), 0);
      return {
        ...prev,
        processes: [
          ...prev.processes,
          {
            key: newKey,
            equipment: { name: "", price: null, powerKw: null, areaM2: null, headcount: 0 },
            powerLoadRatio: null,
            laborRate: 0,
            displayOrder: maxOrder + 10, // 후미 — 사이에 끼우려면 값을 직접 고친다
          },
        ],
      };
    });
  };

  const addRawMaterial = () => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            rawMaterials: [
              ...prev.rawMaterials,
              // id "" → 저장 시 서버가 발급 (견적 materialId 참조 키)
              {
                id: "",
                name: "",
                rawUnitPrice: 0,
                scrapUnitPrice: 0,
                density: 0,
                gPerMm3: 0,
                recoveryRate: DEFAULT_SCRAP_RECOVERY_RATE,
              },
            ],
          }
        : prev
    );
  };

  const addSurface = () => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            surfaceTreatments: [...prev.surfaceTreatments, { id: "", name: "", unitPrice: 0 }],
          }
        : prev
    );
  };

  const deleteProcess = (idx: number) => {
    setDraft((prev) =>
      prev ? { ...prev, processes: prev.processes.filter((_, i) => i !== idx) } : prev
    );
  };

  const deleteRawMaterial = (idx: number) => {
    setDraft((prev) =>
      prev ? { ...prev, rawMaterials: prev.rawMaterials.filter((_, i) => i !== idx) } : prev
    );
  };

  const deleteSurface = (idx: number) => {
    setDraft((prev) =>
      prev
        ? { ...prev, surfaceTreatments: prev.surfaceTreatments.filter((_, i) => i !== idx) }
        : prev
    );
  };

  const setConstant = <K extends keyof typeof draft.constants>(key: K, val: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, constants: { ...prev.constants, [key]: val } };
    });
  };

  const constInput = <K extends keyof typeof draft.constants>(key: K) => (
    <input
      type="number"
      style={inp}
      value={draft.constants[key]}
      disabled={saving}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") {
          // Allow empty intermediate state during editing; keep current value in draft
        } else {
          const parsed = Number.parseFloat(raw);
          setConstant(key, Number.isNaN(parsed) ? draft.constants[key] : parsed);
        }
      }}
      onBlur={(e) => {
        const raw = e.target.value;
        if (raw === "") {
          // Coerce to 0 on blur if still empty
          setConstant(key, 0);
        }
      }}
    />
  );

  return (
    <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 28 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <SectionTitle sub="공정·설비(임율 포함) / 원재료 / 표면처리 / 원가 상수 / 견적 설정">
            원가 마스터
          </SectionTitle>
        </div>
        <div style={{ display: "flex", gap: 10, paddingTop: 2 }}>
          <Pill variant="ghost" size="sm" disabled={saving} onClick={handleRevert}>
            되돌리기
          </Pill>
          <Pill variant="primary" size="sm" disabled={saving} onClick={handleSave}>
            저장
          </Pill>
        </div>
      </div>

      <ScreenHelp items={COST_MASTER_HELP} />

      <Card pad={0}>
        <div style={{ padding: "18px 24px 4px" }}>
          <SectionTitle sub="공정에 배정된 설비 사양과 임율">공정 · 설비 마스터</SectionTitle>
        </div>
        <Table
          headers={[
            "노출순서",
            "공정",
            "장비명",
            "장비가액(원)",
            "전력용량(kW)",
            "설치면적(㎡)",
            "인원",
            "임율(₩/HR)",
            "",
          ]}
        >
          {draft.processes.map((p, i) => (
            // ponytail: index key — no stable id; add/append-only, no reorder
            <tr key={i}>
              {/* 노출순서 — 견적상세 분해표의 공정 컬럼 정렬 기준(작을수록 앞). 저장 시 이 순서로 정렬된다. */}
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={p.displayOrder}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseInt(e.target.value, 10);
                    setProcessDisplayOrder(i, Number.isNaN(parsed) ? 0 : parsed);
                  }}
                />
              </td>
              <td style={tdStyle}>
                <input
                  style={{ ...inp, textAlign: "left" }}
                  value={p.key}
                  disabled={saving}
                  onChange={(e) => setProcessKey(i, e.target.value)}
                />
              </td>
              <td style={tdStyle}>
                <input
                  style={{ ...inp, textAlign: "left" }}
                  value={p.equipment.name}
                  disabled={saving}
                  onChange={(e) => setEquipmentName(i, e.target.value)}
                />
              </td>
              <td style={tdNum}>
                <NumInput
                  value={p.equipment.price}
                  disabled={saving}
                  onChange={(v) => setEquipment(i, { price: v })}
                  style={inp}
                />
              </td>
              <td style={tdNum}>
                <NumInput
                  value={p.equipment.powerKw}
                  disabled={saving}
                  onChange={(v) => setEquipment(i, { powerKw: v })}
                  style={inp}
                />
              </td>
              <td style={tdNum}>
                <NumInput
                  value={p.equipment.areaM2}
                  disabled={saving}
                  onChange={(v) => setEquipment(i, { areaM2: v })}
                  style={inp}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={p.equipment.headcount}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    setEquipment(i, { headcount: Number.isNaN(parsed) ? 0 : parsed });
                  }}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={p.laborRate}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    setProcessLaborRate(i, Number.isNaN(parsed) ? 0 : parsed);
                  }}
                />
              </td>
              <td style={tdNum}>
                <DelBtn
                  onClick={() => deleteProcess(i)}
                  disabled={saving || draft.processes.length === 1}
                />
              </td>
            </tr>
          ))}
        </Table>
        <div style={{ padding: "10px 24px 14px" }}>
          <button
            type="button"
            disabled={saving}
            onClick={addProcess}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: 0,
              color: "var(--brand)",
              cursor: saving ? "default" : "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              padding: 0,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <Icon name="plus" size={14} /> 추가
          </button>
        </div>
      </Card>

      <div
        style={{
          display: "grid",
          // Desktop unchanged (320px track). Mobile: min(100%, 300px) so the track
          // never exceeds the container (no phone overflow) and wraps to one column.
          gridTemplateColumns: mobile
            ? "repeat(auto-fit,minmax(min(100%,300px),1fr))"
            : "repeat(auto-fit,minmax(320px,1fr))",
          gap: 28,
        }}
      >
        <Card pad={0}>
          <div style={{ padding: "18px 24px 4px" }}>
            <SectionTitle sub="₩/g — 견적 표면처리 선택지">표면처리</SectionTitle>
          </div>
          <Table headers={["품명", "단가 (₩/g)", ""]}>
            {draft.surfaceTreatments.map((s, i) => (
              <tr key={s.id || `new-${i}`}>
                <td style={tdStyle}>
                  <input
                    style={{ ...inp, textAlign: "left" }}
                    value={s.name}
                    disabled={saving}
                    onChange={(e) => setSurface(i, { name: e.target.value })}
                  />
                </td>
                <td style={tdNum}>
                  <input
                    type="number"
                    style={inp}
                    value={s.unitPrice}
                    disabled={saving}
                    onChange={(e) => {
                      const parsed = Number.parseFloat(e.target.value);
                      setSurface(i, { unitPrice: Number.isNaN(parsed) ? 0 : parsed });
                    }}
                  />
                </td>
                <td style={tdNum}>
                  <DelBtn onClick={() => deleteSurface(i)} disabled={saving} />
                </td>
              </tr>
            ))}
          </Table>
          <div style={{ padding: "10px 24px 14px" }}>
            <button
              type="button"
              disabled={saving}
              onClick={addSurface}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                border: 0,
                color: "var(--brand)",
                cursor: saving ? "default" : "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
                opacity: saving ? 0.5 : 1,
              }}
            >
              <Icon name="plus" size={14} /> 추가
            </button>
          </div>
        </Card>
      </div>

      <Card pad={0}>
        <div style={{ padding: "18px 24px 4px" }}>
          <SectionTitle sub="견적 재질 선택지 — 단가(₩/g)와 비중, 단중은 비중/1000 자동 · 회수율은 재질 선택 시 견적에 기본값으로 반영">
            원재료
          </SectionTitle>
        </div>
        <Table
          headers={[
            "품명",
            "원소재 단가 (₩/g)",
            "스크랩 단가 (₩/g)",
            "회수율 (%)",
            "비중 (g/cm³)",
            "단중 (g/㎣)",
            "",
          ]}
        >
          {draft.rawMaterials.map((r, i) => (
            <tr key={r.id || `new-${i}`}>
              <td style={tdStyle}>
                <input
                  style={{ ...inp, textAlign: "left" }}
                  value={r.name}
                  disabled={saving}
                  onChange={(e) => setRawMaterial(i, { name: e.target.value })}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={r.rawUnitPrice}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    setRawMaterial(i, { rawUnitPrice: Number.isNaN(parsed) ? 0 : parsed });
                  }}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={r.scrapUnitPrice}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    setRawMaterial(i, { scrapUnitPrice: Number.isNaN(parsed) ? 0 : parsed });
                  }}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  aria-label={`${r.name} 회수율`}
                  style={inp}
                  value={r.recoveryRate}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    setRawMaterial(i, { recoveryRate: Number.isNaN(parsed) ? 0 : parsed });
                  }}
                />
              </td>
              <td style={tdNum}>
                <input
                  type="number"
                  style={inp}
                  value={r.density}
                  disabled={saving}
                  onChange={(e) => {
                    const parsed = Number.parseFloat(e.target.value);
                    const density = Number.isNaN(parsed) ? 0 : parsed;
                    setRawMaterial(i, { density, gPerMm3: density / 1000 });
                  }}
                />
              </td>
              <td style={tdNum}>{(r.density / 1000).toFixed(5)}</td>
              <td style={tdNum}>
                <DelBtn onClick={() => deleteRawMaterial(i)} disabled={saving} />
              </td>
            </tr>
          ))}
        </Table>
        <div style={{ padding: "10px 24px 14px" }}>
          <button
            type="button"
            disabled={saving}
            onClick={addRawMaterial}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: 0,
              color: "var(--brand)",
              cursor: saving ? "default" : "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              padding: 0,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <Icon name="plus" size={14} /> 추가
          </button>
        </div>
      </Card>

      <Card pad={0}>
        <div style={{ padding: "18px 24px 4px" }}>
          <SectionTitle sub="전역 1행">원가 상수</SectionTitle>
        </div>
        <Table headers={["항목", "값"]}>
          <tr>
            <td style={tdStyle}>전력단가 (₩/kWh)</td>
            <td style={tdNum}>{constInput("powerUnitPrice")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>부대설비비율</td>
            <td style={tdNum}>{constInput("auxEquipmentRatio")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>건축비용 (₩/㎡)</td>
            <td style={tdNum}>{constInput("buildingCost")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>건물년수</td>
            <td style={tdNum}>{constInput("buildingYears")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>설비년수 (전용)</td>
            <td style={tdNum}>{constInput("equipmentYearsDedicated")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>설비년수 (범용)</td>
            <td style={tdNum}>{constInput("equipmentYearsGeneral")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>수선비율</td>
            <td style={tdNum}>{constInput("repairRatio")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>일작업시간 (h)</td>
            <td style={tdNum}>{constInput("dailyWorkHours")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>년작업일수 (일)</td>
            <td style={tdNum}>{constInput("yearlyWorkDays")}</td>
          </tr>
          <tr>
            <td style={tdStyle}>간접경비율</td>
            <td style={tdNum}>{constInput("indirectExpenseRatio")}</td>
          </tr>
        </Table>
      </Card>

      <Card pad={0}>
        <div style={{ padding: "18px 24px 4px" }}>
          <SectionTitle sub="견적 계산 기본 비율 (구 CNC 설정 — 간접경비율은 위 원가 상수 사용)">
            견적 설정
          </SectionTitle>
        </div>
        <Table headers={["항목", "값"]}>
          <tr>
            <td style={tdStyle}>여유율</td>
            <td style={tdNum}>
              <input
                type="number"
                style={inp}
                value={draft.quoteSettings.allowance}
                disabled={saving}
                onChange={(e) => {
                  const parsed = Number.parseFloat(e.target.value);
                  setQuoteSetting("allowance", Number.isNaN(parsed) ? 0 : parsed);
                }}
              />
            </td>
          </tr>
          <tr>
            <td style={tdStyle}>일반관리비율 (내작)</td>
            <td style={tdNum}>
              <input
                type="number"
                style={inp}
                value={draft.quoteSettings.generalAdminRate}
                disabled={saving}
                onChange={(e) => {
                  const parsed = Number.parseFloat(e.target.value);
                  setQuoteSetting("generalAdminRate", Number.isNaN(parsed) ? 0 : parsed);
                }}
              />
            </td>
          </tr>
          <tr>
            <td style={tdStyle}>재료관리비율</td>
            <td style={tdNum}>
              <input
                type="number"
                style={inp}
                value={draft.quoteSettings.materialAdminRate}
                disabled={saving}
                onChange={(e) => {
                  const parsed = Number.parseFloat(e.target.value);
                  setQuoteSetting("materialAdminRate", Number.isNaN(parsed) ? 0 : parsed);
                }}
              />
            </td>
          </tr>
        </Table>
      </Card>
    </div>
  );
};
